import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ILegalTransactionGwGEntry } from '../../../Interfaces/ILegalTransactionGwGEntry';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import {
  Delete,
  Edit,
  Check,
  Close,
  InsertDriveFile,
} from '@mui/icons-material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Tooltip } from '@mui/material';

interface IProps {
  gwgEntryObject: ILegalTransactionGwGEntry;
  gwgEntryArray: ILegalTransactionGwGEntry[];
  setGwgEntryArray: Dispatch<SetStateAction<ILegalTransactionGwGEntry[]>>;
  onEdit: (entry: ILegalTransactionGwGEntry) => void;
  onReject: (entry: ILegalTransactionGwGEntry) => void;
  onAccept: (entry: ILegalTransactionGwGEntry) => void;
}

export const GwGEntry: React.FC<IProps> = ({
  gwgEntryObject,
  gwgEntryArray,
  setGwgEntryArray,
  onEdit,
  onReject,
  onAccept,
}) => {
  //console.log(gwgEntryArray);
  const [entry, setEntry] = useState(gwgEntryObject);

  useEffect(() => {
    setEntry(gwgEntryObject);
  }, [gwgEntryObject]);

  useEffect(() => {
    setGwgEntryArray(prev =>
      prev.map(e =>
        e.idLegalTransactionGwGEntry === entry.idLegalTransactionGwGEntry
          ? entry
          : e
      )
    );
  }, [entry]);

  const removeMew = () => {
    setGwgEntryArray(
      gwgEntryArray.filter(
        x => x.idLegalTransactionGwGEntry !== entry.idLegalTransactionGwGEntry
      )
    );
  };

  const chipLabel = useMemo(() => {
    switch (entry.idLegalTransactionGwGEntryState) {
      case 1:
        return 'Prüfung';
      case 2:
        return 'Geprüft';
      case 3:
        return 'Abgelehnt';
      default:
        return '';
    }
  }, [entry.idLegalTransactionGwGEntryState]);

  return (
    <>
      <Card variant='outlined' sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            pt: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant='caption'
              sx={
                !entry.title
                  ? { fontStyle: 'italic', color: 'text.secondary' }
                  : {}
              }
            >
              {entry.title || 'Kein Titel'}
            </Typography>

            {entry.DocumentArray.length > 0 && (
              <Chip
                icon={<InsertDriveFile sx={{ fontSize: 14 }} />}
                label={entry.DocumentArray.length}
                variant='outlined'
                onClick={() => onEdit(entry)}
                clickable
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': { marginLeft: 0.2 },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!entry.isPaid && (
              <>
                <Chip
                  label={entry.wasPaid ? 'Bezahlt' : 'Nicht bezahlt'}
                  size='small'
                  variant='outlined'
                  color={entry.wasPaid ? 'success' : 'error'}
                />

                <Tooltip title='Als nicht bezahlt markieren'>
                  <IconButton
                    aria-label='Als nicht bezahlt markieren'
                    onClick={() =>
                      setEntry(prev => ({
                        ...prev,
                        wasPaid: false,
                      }))
                    }
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title='Als bezahlt markieren'>
                  <IconButton
                    aria-label='Als bezahlt markieren'
                    onClick={() =>
                      setEntry(prev => ({
                        ...prev,
                        wasPaid: true,
                      }))
                    }
                  >
                    <Check sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {Boolean(entry.isPaid) && (
              <>
                <Chip
                  label={chipLabel}
                  size='small'
                  variant='outlined'
                  color={
                    entry.idLegalTransactionGwGEntryState === 1
                      ? 'warning'
                      : entry.idLegalTransactionGwGEntryState === 2
                        ? 'success'
                        : 'error'
                  }
                />

                {/* Ablehnen */}
                <Tooltip title='Als abgelehnt markieren'>
                  <IconButton
                    aria-label='Ablehnen'
                    onClick={() => onReject(entry)}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                {/* Als erledigt markieren */}
                <Tooltip title='Als erledigt markieren'>
                  <IconButton
                    aria-label='Als erledigt markieren'
                    onClick={() => onAccept(entry)}
                  >
                    <Check sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                {/* Auf Prüfung setzen */}
                <Tooltip title='Auf in Püfung setzen'>
                  <IconButton
                    aria-label='Auf Prüfung setzen'
                    onClick={() =>
                      setEntry(prev => ({
                        ...prev,
                        idLegalTransactionGwGEntryState: 1,
                      }))
                    }
                  >
                    <QuestionMarkIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title='Bearbeiten'>
              <IconButton onClick={() => onEdit(entry)} aria-label='Bearbeiten'>
                <Edit sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title='Löschen'>
              <IconButton onClick={removeMew} aria-label='Löschen'>
                <Delete sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <CardContent>
          <Grid container>
            <Grid item sm={entry.isPaid ? 12 : 4}>
              <CustomeTextField
                label={entry.isPaid ? 'Einzahlung' : 'Ablösebetrag'}
                attr='currentValue'
                object={entry}
                setObject={setEntry}
                type='euro'
              />
            </Grid>

            {!entry.isPaid && (
              <>
                <Grid item sm={4}>
                  <CustomeTextField
                    label='Tagessatz'
                    attr='currentRate'
                    object={entry}
                    setObject={setEntry}
                    type='euro'
                  />
                </Grid>
                <Grid item sm={4}>
                  <CustomeTextField
                    label='Nebenkosten'
                    attr='extraCosts'
                    object={entry}
                    setObject={setEntry}
                    type='euro'
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};
